import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, X, Eye, DollarSign, TrendingUp, TrendingDown,
  PieChart, BarChart3, AlertTriangle, CheckCircle, Calendar, Building2,
  FolderKanban, Tag, Filter, FileText, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const Budgets = () => {
  const [activeTab, setActiveTab] = useState('budgets');
  const [budgets, setBudgets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [budgetDetails, setBudgetDetails] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    name_arabic: '',
    budget_type: 'project',
    period: 'yearly',
    year: new Date().getFullYear(),
    month: null,
    quarter: null,
    allocated_amount: '',
    project_id: '',
    department: '',
    category: '',
    notes: ''
  });

  const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const quarters = ['Q1 (Jan-Mar)', 'Q2 (Apr-Jun)', 'Q3 (Jul-Sep)', 'Q4 (Oct-Dec)'];

  useEffect(() => {
    fetchData();
  }, [filterType, filterYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBudgets(),
        fetchSummary(),
        fetchBalanceSheet(),
        fetchAlerts(),
        fetchProjects(),
        fetchDepartments(),
        fetchCategories()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgets = async () => {
    try {
      let url = '/budgets';
      const params = new URLSearchParams();
      if (filterType) params.append('budget_type', filterType);
      if (filterYear) params.append('year', filterYear);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axiosInstance.get(url);
      setBudgets(response.data);
    } catch (error) {
      console.error('Failed to fetch budgets');
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axiosInstance.get(`/budgets/summary?year=${filterYear}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary');
    }
  };

  const fetchBalanceSheet = async () => {
    try {
      const response = await axiosInstance.get('/budgets/balance-sheet');
      setBalanceSheet(response.data);
    } catch (error) {
      console.error('Failed to fetch balance sheet');
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axiosInstance.get('/budgets/alerts/over-budget');
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axiosInstance.get('/budgets/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/budgets/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchBudgetDetails = async (budgetId) => {
    try {
      const response = await axiosInstance.get(`/budgets/${budgetId}/details`);
      setBudgetDetails(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error('Failed to fetch budget details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        allocated_amount: parseFloat(formData.allocated_amount) || 0,
        month: formData.period === 'monthly' ? parseInt(formData.month) : null,
        quarter: formData.period === 'quarterly' ? parseInt(formData.quarter) : null,
        project_id: formData.budget_type === 'project' ? formData.project_id : null,
        department: formData.budget_type === 'department' ? formData.department : null,
        category: formData.budget_type === 'category' ? formData.category : null,
      };

      if (editingBudget) {
        await axiosInstance.put(`/budgets/${editingBudget.id}`, submitData);
        toast.success('Budget updated successfully');
      } else {
        await axiosInstance.post('/budgets', submitData);
        toast.success('Budget created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg).join(', '));
      } else {
        toast.error(errorDetail || 'Operation failed');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this budget?')) return;
    try {
      await axiosInstance.delete(`/budgets/${id}`);
      toast.success('Budget deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete budget');
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setFormData({
      name: budget.name || '',
      name_arabic: budget.name_arabic || '',
      budget_type: budget.budget_type || 'project',
      period: budget.period || 'yearly',
      year: budget.year || new Date().getFullYear(),
      month: budget.month || null,
      quarter: budget.quarter || null,
      allocated_amount: budget.allocated_amount || '',
      project_id: budget.project_id || '',
      department: budget.department || '',
      category: budget.category || '',
      notes: budget.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_arabic: '',
      budget_type: 'project',
      period: 'yearly',
      year: new Date().getFullYear(),
      month: null,
      quarter: null,
      allocated_amount: '',
      project_id: '',
      department: '',
      category: '',
      notes: ''
    });
    setEditingBudget(null);
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 100) return 'text-red-600 bg-red-100';
    if (percentage >= 80) return 'text-orange-600 bg-orange-100';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-orange-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="page-title">Budget & Balance Sheet / الميزانية</h2>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary"
          data-testid="new-budget-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Budget
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('budgets')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
            activeTab === 'budgets' ? 'bg-white text-purple-600 shadow-md' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <PieChart className="w-5 h-5" />
          Budgets / الميزانيات
        </button>
        <button
          onClick={() => setActiveTab('balance-sheet')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
            activeTab === 'balance-sheet' ? 'bg-white text-purple-600 shadow-md' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <BarChart3 className="w-5 h-5" />
          Balance Sheet / الميزانية العمومية
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
            activeTab === 'alerts' ? 'bg-white text-purple-600 shadow-md' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <AlertTriangle className="w-5 h-5" />
          Alerts / التنبيهات
          {alerts.length > 0 && (
            <span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs">{alerts.length}</span>
          )}
        </button>
      </div>

      {/* Budgets Tab */}
      {activeTab === 'budgets' && (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                <p className="text-purple-100 text-sm">Total Allocated</p>
                <p className="text-2xl font-bold">{summary.total_allocated?.toLocaleString()} SAR</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                <p className="text-blue-100 text-sm">Total Spent</p>
                <p className="text-2xl font-bold">{summary.total_spent?.toLocaleString()} SAR</p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                <p className="text-green-100 text-sm">Remaining</p>
                <p className="text-2xl font-bold">{summary.total_remaining?.toLocaleString()} SAR</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
                <p className="text-orange-100 text-sm">Utilization</p>
                <p className="text-2xl font-bold">{summary.utilization_percentage}%</p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
                <p className="text-red-100 text-sm">Over Budget</p>
                <p className="text-2xl font-bold">{summary.over_budget_count}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-4 items-center flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="form-input py-2"
              >
                <option value="">All Types</option>
                <option value="project">Project Budgets</option>
                <option value="department">Department Budgets</option>
                <option value="category">Category Budgets</option>
              </select>
            </div>
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(parseInt(e.target.value))}
              className="form-input py-2"
            >
              {[2023, 2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Budget Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {budgets.length > 0 ? (
              budgets.map((budget) => (
                <div key={budget.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        budget.budget_type === 'project' ? 'bg-purple-100 text-purple-700' :
                        budget.budget_type === 'department' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {budget.budget_type === 'project' ? 'Project' : 
                         budget.budget_type === 'department' ? 'Department' : 'Category'}
                      </span>
                      <h3 className="font-bold text-lg mt-2">{budget.name}</h3>
                      {budget.name_arabic && <p className="text-gray-500 text-sm">{budget.name_arabic}</p>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => fetchBudgetDetails(budget.id)} className="btn-icon" title="View Details">
                        <Eye className="w-4 h-4 text-green-600" />
                      </button>
                      <button onClick={() => handleEdit(budget)} className="btn-icon" title="Edit">
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      <button onClick={() => handleDelete(budget.id)} className="btn-icon delete" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Allocated:</span>
                      <span className="font-semibold">{budget.allocated_amount?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Spent:</span>
                      <span className="font-semibold text-blue-600">{budget.spent_amount?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Remaining:</span>
                      <span className={`font-semibold ${budget.remaining_amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {budget.remaining_amount?.toLocaleString()} SAR
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Utilization</span>
                        <span className={`font-semibold px-2 py-0.5 rounded ${getUtilizationColor(budget.utilization_percentage)}`}>
                          {budget.utilization_percentage}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${getProgressColor(budget.utilization_percentage)}`}
                          style={{ width: `${Math.min(budget.utilization_percentage, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                      <Calendar className="w-3 h-3" />
                      <span>
                        {budget.period === 'monthly' ? `${months[budget.month - 1]} ${budget.year}` :
                         budget.period === 'quarterly' ? `Q${budget.quarter} ${budget.year}` :
                         budget.year}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-12 text-gray-500">
                <PieChart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No budgets found. Click "New Budget" to create one.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Balance Sheet Tab */}
      {activeTab === 'balance-sheet' && balanceSheet && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Assets */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <ArrowUpRight className="w-6 h-6" />
                Assets / الأصول
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Current Assets */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Current Assets / أصول متداولة
                </h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between">
                    <span>Cash & Bank / النقدية والبنوك</span>
                    <span className="font-semibold">{balanceSheet.assets.current_assets.cash_and_bank?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Accounts Receivable / ذمم مدينة</span>
                    <span className="font-semibold">{balanceSheet.assets.current_assets.accounts_receivable?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Inventory / المخزون</span>
                    <span className="font-semibold">{balanceSheet.assets.current_assets.inventory?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Employee Loans / سلف الموظفين</span>
                    <span className="font-semibold">{balanceSheet.assets.current_assets.employee_loans?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Other Current Assets / أصول متداولة أخرى</span>
                    <span className="font-semibold">{balanceSheet.assets.current_assets.other_current_assets?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-bold text-blue-600">
                    <span>Total Current Assets</span>
                    <span>{balanceSheet.assets.current_assets.total?.toLocaleString()} SAR</span>
                  </div>
                </div>
              </div>

              {/* Fixed Assets */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Fixed Assets / أصول ثابتة
                </h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between">
                    <span>Property & Equipment / ممتلكات ومعدات</span>
                    <span className="font-semibold">{balanceSheet.assets.fixed_assets.property_equipment?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between text-orange-600">
                    <span>Less: Accumulated Depreciation / مجمع الإهلاك</span>
                    <span className="font-semibold">({balanceSheet.assets.fixed_assets.accumulated_depreciation?.toLocaleString()}) SAR</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-bold text-blue-600">
                    <span>Net Fixed Assets / صافي الأصول الثابتة</span>
                    <span>{balanceSheet.assets.fixed_assets.net_fixed_assets?.toLocaleString()} SAR</span>
                  </div>
                </div>
              </div>

              {/* Total Assets */}
              <div className="bg-blue-100 rounded-lg p-4">
                <div className="flex justify-between text-xl font-bold text-blue-700">
                  <span>Total Assets / إجمالي الأصول</span>
                  <span>{balanceSheet.assets.total_assets?.toLocaleString()} SAR</span>
                </div>
              </div>
            </div>
          </div>

          {/* Liabilities & Equity */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <ArrowDownRight className="w-6 h-6" />
                Liabilities & Equity / الخصوم وحقوق الملكية
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Current Liabilities */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Current Liabilities / خصوم متداولة</h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between">
                    <span>Accounts Payable / ذمم دائنة</span>
                    <span className="font-semibold">{balanceSheet.liabilities.current_liabilities.accounts_payable?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-bold text-red-600">
                    <span>Total Current Liabilities</span>
                    <span>{balanceSheet.liabilities.current_liabilities.total?.toLocaleString()} SAR</span>
                  </div>
                </div>
              </div>

              {/* Total Liabilities */}
              <div className="bg-red-50 rounded-lg p-3">
                <div className="flex justify-between font-bold text-red-600">
                  <span>Total Liabilities / إجمالي الخصوم</span>
                  <span>{balanceSheet.liabilities.total_liabilities?.toLocaleString()} SAR</span>
                </div>
              </div>

              {/* Equity */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Equity / حقوق الملكية</h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between">
                    <span>Retained Earnings / أرباح مبقاة</span>
                    <span className="font-semibold">{balanceSheet.equity.retained_earnings?.toLocaleString()} SAR</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-bold text-green-600">
                    <span>Total Equity</span>
                    <span>{balanceSheet.equity.total_equity?.toLocaleString()} SAR</span>
                  </div>
                </div>
              </div>

              {/* Journal Summary */}
              {balanceSheet.journal_summary && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Journal Summary / ملخص القيود</h4>
                  <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between">
                      <span>Total Debit / إجمالي المدين</span>
                      <span className="font-semibold text-blue-600">{balanceSheet.journal_summary.total_debit?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Credit / إجمالي الدائن</span>
                      <span className="font-semibold text-blue-600">{balanceSheet.journal_summary.total_credit?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span>Salary Expenses / مصروفات الرواتب</span>
                      <span className="font-semibold">{balanceSheet.journal_summary.salary_expenses?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Loan Disbursements / صرف السلف</span>
                      <span className="font-semibold">{balanceSheet.journal_summary.loan_disbursements?.toLocaleString()} SAR</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Expenses */}
              {balanceSheet.expenses && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Expenses / المصاريف ({balanceSheet.expenses.count || 0})</h4>
                  <div className="bg-red-50 rounded-lg p-3 space-y-2">
                    {balanceSheet.expenses.by_category && Object.keys(balanceSheet.expenses.by_category).length > 0 ? (
                      <>
                        {Object.entries(balanceSheet.expenses.by_category).map(([category, amount]) => (
                          <div key={category} className="flex justify-between">
                            <span>{category}</span>
                            <span className="font-semibold">{amount?.toLocaleString()} SAR</span>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="flex justify-between text-gray-400">
                        <span>No expenses recorded</span>
                        <span>0 SAR</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-2 font-bold text-red-600">
                      <span>Total Expenses / إجمالي المصاريف</span>
                      <span>{balanceSheet.expenses.total?.toLocaleString()} SAR</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Depreciation */}
              {balanceSheet.depreciation && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Depreciation / الإهلاك</h4>
                  <div className="bg-orange-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between">
                      <span>Fixed Assets (Gross) / الأصول الثابتة (إجمالي)</span>
                      <span className="font-semibold">{balanceSheet.depreciation.fixed_assets_gross?.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between text-orange-600">
                      <span>Accumulated Depreciation / مجمع الإهلاك</span>
                      <span className="font-semibold">({balanceSheet.depreciation.accumulated_depreciation?.toLocaleString()}) SAR</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 font-bold">
                      <span>Net Fixed Assets / صافي الأصول الثابتة</span>
                      <span>{balanceSheet.depreciation.fixed_assets_net?.toLocaleString()} SAR</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Total Liabilities & Equity */}
              <div className="bg-purple-100 rounded-lg p-4">
                <div className="flex justify-between text-xl font-bold text-purple-700">
                  <span>Total Liabilities & Equity</span>
                  <span>{balanceSheet.total_liabilities_and_equity?.toLocaleString()} SAR</span>
                </div>
              </div>
            </div>
          </div>

          {/* Balance Check */}
          <div className="col-span-2">
            <div className={`rounded-xl p-4 flex items-center justify-center gap-3 ${
              balanceSheet.assets.total_assets === balanceSheet.total_liabilities_and_equity
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {balanceSheet.assets.total_assets === balanceSheet.total_liabilities_and_equity ? (
                <>
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-semibold text-lg">Balance Sheet is Balanced! / الميزانية متوازنة</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-6 h-6" />
                  <span className="font-semibold text-lg">Balance Sheet has a discrepancy / يوجد فرق في الميزانية</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {alerts.length > 0 ? (
            alerts.map((alert) => (
              <div
                key={alert.budget_id}
                className={`rounded-xl p-4 flex items-center justify-between ${
                  alert.alert_level === 'critical' ? 'bg-red-50 border-2 border-red-200' : 'bg-orange-50 border-2 border-orange-200'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    alert.alert_level === 'critical' ? 'bg-red-500' : 'bg-orange-500'
                  } text-white`}>
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">{alert.name}</h4>
                    <p className="text-sm text-gray-600">{alert.budget_number}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Allocated</p>
                      <p className="font-semibold">{alert.allocated?.toLocaleString()} SAR</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Spent</p>
                      <p className="font-semibold text-blue-600">{alert.spent?.toLocaleString()} SAR</p>
                    </div>
                    <div className={`px-4 py-2 rounded-lg font-bold text-xl ${
                      alert.alert_level === 'critical' ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'
                    }`}>
                      {alert.utilization}%
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-semibold">All budgets are within limits!</p>
              <p>No alerts at this time.</p>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editingBudget ? 'Edit Budget' : 'New Budget'}
              </h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Budget Type */}
                <div className="col-span-2">
                  <label className="form-label">Budget Type / نوع الميزانية</label>
                  <div className="flex gap-3">
                    {[
                      { value: 'project', label: 'Project', icon: FolderKanban, color: 'purple' },
                      { value: 'department', label: 'Department', icon: Building2, color: 'blue' },
                      { value: 'category', label: 'Category', icon: Tag, color: 'green' }
                    ].map((type) => (
                      <label
                        key={type.value}
                        className={`flex-1 p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-2 ${
                          formData.budget_type === type.value
                            ? `border-${type.color}-500 bg-${type.color}-50`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="budget_type"
                          value={type.value}
                          checked={formData.budget_type === type.value}
                          onChange={(e) => setFormData({ ...formData, budget_type: e.target.value })}
                          className="sr-only"
                        />
                        <type.icon className={`w-5 h-5 ${formData.budget_type === type.value ? `text-${type.color}-600` : 'text-gray-400'}`} />
                        <span className="font-medium">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="form-label">Name (English)</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">الاسم (عربي)</label>
                  <input
                    type="text"
                    value={formData.name_arabic}
                    onChange={(e) => setFormData({ ...formData, name_arabic: e.target.value })}
                    className="form-input"
                    dir="rtl"
                  />
                </div>

                {/* Conditional Fields based on Budget Type */}
                {formData.budget_type === 'project' && (
                  <div className="col-span-2">
                    <label className="form-label">Project / المشروع</label>
                    <select
                      value={formData.project_id}
                      onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                      className="form-input"
                      required
                    >
                      <option value="">Select Project</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.budget_type === 'department' && (
                  <div className="col-span-2">
                    <label className="form-label">Department / القسم</label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="form-input"
                      required
                    >
                      <option value="">Select Department</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.name} / {d.name_arabic}</option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.budget_type === 'category' && (
                  <div className="col-span-2">
                    <label className="form-label">Category / الفئة</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="form-input"
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name} / {c.name_arabic}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Period */}
                <div>
                  <label className="form-label">Period / الفترة</label>
                  <select
                    value={formData.period}
                    onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                    className="form-input"
                  >
                    <option value="yearly">Yearly / سنوي</option>
                    <option value="quarterly">Quarterly / ربع سنوي</option>
                    <option value="monthly">Monthly / شهري</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Year / السنة</label>
                  <select
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    className="form-input"
                  >
                    {[2023, 2024, 2025, 2026].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                {formData.period === 'monthly' && (
                  <div>
                    <label className="form-label">Month / الشهر</label>
                    <select
                      value={formData.month || ''}
                      onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                      className="form-input"
                      required
                    >
                      <option value="">Select Month</option>
                      {months.map((month, idx) => (
                        <option key={idx} value={idx + 1}>{month}</option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.period === 'quarterly' && (
                  <div>
                    <label className="form-label">Quarter / الربع</label>
                    <select
                      value={formData.quarter || ''}
                      onChange={(e) => setFormData({ ...formData, quarter: parseInt(e.target.value) })}
                      className="form-input"
                      required
                    >
                      <option value="">Select Quarter</option>
                      {quarters.map((quarter, idx) => (
                        <option key={idx} value={idx + 1}>{quarter}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Allocated Amount */}
                <div className="col-span-2">
                  <label className="form-label">Allocated Amount (SAR) / المبلغ المخصص</label>
                  <input
                    type="number"
                    value={formData.allocated_amount}
                    onChange={(e) => setFormData({ ...formData, allocated_amount: e.target.value })}
                    className="form-input"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* Notes */}
                <div className="col-span-2">
                  <label className="form-label">Notes / ملاحظات</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="form-input"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">
                  {editingBudget ? 'Update Budget' : 'Create Budget'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Budget Details Modal */}
      {showDetailsModal && budgetDetails && (
        <div className="modal-overlay">
          <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-white pb-4 border-b">
              <div>
                <h3 className="text-xl font-bold">{budgetDetails.budget?.name}</h3>
                <p className="text-gray-500">{budgetDetails.budget?.name_arabic}</p>
              </div>
              <button onClick={() => { setShowDetailsModal(false); setBudgetDetails(null); }} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <p className="text-purple-600 text-sm">Allocated</p>
                <p className="text-xl font-bold text-purple-700">{budgetDetails.budget?.allocated_amount?.toLocaleString()} SAR</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-blue-600 text-sm">Total Spent</p>
                <p className="text-xl font-bold text-blue-700">{budgetDetails.total_spent?.toLocaleString()} SAR</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <p className="text-orange-600 text-sm">Expenses ({budgetDetails.expenses?.count || 0})</p>
                <p className="text-xl font-bold text-orange-700">{budgetDetails.expenses?.total?.toLocaleString()} SAR</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-green-600 text-sm">Payroll ({budgetDetails.payroll?.count || 0})</p>
                <p className="text-xl font-bold text-green-700">{budgetDetails.payroll?.total?.toLocaleString()} SAR</p>
              </div>
            </div>

            {/* Expenses Section */}
            <div className="mb-6">
              <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-orange-600" />
                Expenses / المصروفات
              </h4>
              {budgetDetails.expenses?.items?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-orange-50">
                      <tr>
                        <th className="p-3 text-left">Date</th>
                        <th className="p-3 text-left">Category</th>
                        <th className="p-3 text-left">Description</th>
                        <th className="p-3 text-left">Vendor</th>
                        <th className="p-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {budgetDetails.expenses.items.map((expense, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3">{expense.date}</td>
                          <td className="p-3">{expense.category}</td>
                          <td className="p-3">{expense.description}</td>
                          <td className="p-3">{expense.vendor}</td>
                          <td className="p-3 text-right font-semibold text-orange-600">{expense.amount?.toLocaleString()} SAR</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-orange-100 font-bold">
                      <tr>
                        <td colSpan="4" className="p-3 text-right">Total Expenses:</td>
                        <td className="p-3 text-right text-orange-700">{budgetDetails.expenses.total?.toLocaleString()} SAR</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">No expenses found for this budget period</p>
              )}
            </div>

            {/* Payroll Section */}
            <div>
              <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Payroll / الرواتب
              </h4>
              {budgetDetails.payroll?.items?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-50">
                      <tr>
                        <th className="p-3 text-left">Payroll #</th>
                        <th className="p-3 text-left">Employee</th>
                        <th className="p-3 text-left">Period</th>
                        <th className="p-3 text-left">Paid Date</th>
                        <th className="p-3 text-right">Net Salary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {budgetDetails.payroll.items.map((payroll, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{payroll.payroll_number}</td>
                          <td className="p-3">{payroll.employee_name}</td>
                          <td className="p-3">{payroll.period_start} - {payroll.period_end}</td>
                          <td className="p-3">{payroll.paid_date}</td>
                          <td className="p-3 text-right font-semibold text-green-600">{payroll.net_salary?.toLocaleString()} SAR</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-green-100 font-bold">
                      <tr>
                        <td colSpan="4" className="p-3 text-right">Total Payroll:</td>
                        <td className="p-3 text-right text-green-700">{budgetDetails.payroll.total?.toLocaleString()} SAR</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">No payroll found for this budget period</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budgets;
